// src/pages/posts/PostErrores.jsx
import React from "react";
import BlogHero from "../blog/BlogHero";
import BlogHeader from "../blog/BlogHeader";
import BlogFooter from "../blog/BlogFooter";

import BlogQuote from "../blog/BlogQuote";
import BlogImage from "../blog/BlogImage";
import ejemploImg from "../../assets/blog/errores-ejemplo.png";
import heroImg from "../../assets/blog/errores-hero.jpg";
import {
	Users,
	ExternalLink,
	BarChart3,
	HeartHandshake,
	ShoppingCart,
	Scissors,
	ShoppingBag,
	Dumbbell,
} from "lucide-react";
import { Bot, Smartphone, Zap, Globe, PieChart } from "lucide-react";

export default function PostErrores() {
	return (
		<div className="font-poppins text-gray-800">
			<BlogHeader />
			<BlogHero
				title="Errores comunes al digitalizar un negocio (y cómo evitarlos)"
				subtitle="Digitalizarse no es solo tener una web. Descubre qué fallos cometen muchos negocios y cómo solucionarlos."
				image={heroImg}
			/>

			<main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
				{/* Introducción con datos */}
				<section>
					<p className="text-lg leading-relaxed mb-6">
						Los clientes esperan resolver cosas desde el móvil: pedir, reservar
						o chatear. Aun así, muchos negocios tropiezan al dar el salto
						digital.
					</p>
					<p className="text-lg leading-relaxed">
						Digitalizarse no significa simplemente abrir un perfil en redes
						sociales o tener una web estática. Implica ofrecer{" "}
						<strong>experiencias reales desde el móvil</strong> y aprovechar la
						tecnología para fidelizar y crecer.
					</p>
				</section>

				{/* Errores frecuentes */}
				<section>
					<h2 className="text-2xl font-bold mb-6">Los errores más comunes</h2>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<Users className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>No priorizar al cliente:</strong> apps o webs
								complicadas, lentas o sin diseño profesional generan abandono.
							</div>
						</li>

						<li className="flex items-start">
							<ExternalLink className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Depender solo de terceros:</strong> confiar en
								marketplaces o redes sociales significa ceder datos y pagar
								comisiones.
							</div>
						</li>

						<li className="flex items-start">
							<BarChart3 className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>No medir resultados:</strong> sin estadísticas claras es
								imposible saber qué funciona y qué mejorar.
							</div>
						</li>

						<li className="flex items-start">
							<HeartHandshake className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Olvidar la fidelización:</strong> captar nuevos clientes
								cuesta 5 veces más que retener a los actuales.
							</div>
						</li>
					</ul>
				</section>
				{/* Ejemplo visual */}
				<BlogImage
					src={ejemploImg}
					alt="Ejemplo de app en diseño"
					caption="En Applaby combinamos diseño profesional y usabilidad para que tu app se adapte a tu negocio: restaurantes, tiendas o entrenadores."
				/>

				{/* Consejos prácticos */}
				<section>
					<h2 className="text-2xl font-bold mb-4">Cómo evitar estos errores</h2>
					<p className="text-lg leading-relaxed mb-6">
						La clave está en empezar por lo esencial y elegir una plataforma que
						te acompañe. Con <strong>Applaby</strong> no necesitas invertir
						miles de euros ni depender de agencias externas. Tienes:
					</p>
					<ul className="list-disc pl-6 space-y-3 text-lg">
						<li>App y web listas en pocos días.</li>
						<li>Estadísticas para entender a tus clientes.</li>
						<li>Reservas y pedidos online integrados.</li>
						<li>Notificaciones push y cupones de fidelización.</li>
					</ul>
				</section>

				<section>
					<h2 className="text-2xl font-bold mb-6">Ejemplos prácticos de uso</h2>
					<p className="text-lg leading-relaxed mb-6">
						Cada negocio utiliza su app de manera distinta según sus
						necesidades. Aquí tienes algunos escenarios reales:
					</p>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<ShoppingCart className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Restaurante local:</strong> recibe pedidos sin
								comisiones, organiza reservas online y envía promociones de
								temporada.
							</div>
						</li>
						<li className="flex items-start">
							<Scissors className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Barbería o salón de belleza:</strong> gestiona citas con
								confirmaciones automáticas y ofrece bonos digitales.
							</div>
						</li>
						<li className="flex items-start">
							<ShoppingBag className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Tienda online:</strong> vende directamente a sus
								clientes, con notificaciones de nuevos productos y descuentos
								exclusivos.
							</div>
						</li>
						<li className="flex items-start">
							<Dumbbell className="w-7 h-7 sm:w-6 sm:h-6 text-[#43bd98] mr-3 mt-1 flex-shrink-0" />
							<div>
								<strong>Entrenador personal:</strong> agenda entrenamientos,
								comparte material y ofrece planes adaptados.
							</div>
						</li>
					</ul>
				</section>

				<section>
					<h2 className="text-2xl font-bold mb-4">
						Tendencias en la digitalización de negocios
					</h2>
					<p className="text-lg leading-relaxed mb-6">
						El futuro de los negocios digitales no se limita a tener una app:
						evoluciona hacia experiencias más personalizadas, rápidas y
						conectadas. Estas son algunas de las tendencias que ya están
						marcando el camino:
					</p>
					<ul className="space-y-4 text-lg text-gray-700">
						<li>
							<strong>Automatización:</strong> reservas y pedidos que se
							gestionan solos, reduciendo carga de trabajo.
						</li>
						<li>
							<strong>Personalización:</strong> promociones y contenidos
							adaptados al historial de cada cliente.
						</li>
						<li>
							<strong>Omnicanalidad:</strong> una experiencia fluida en móvil,
							tablet y ordenador.
						</li>
					</ul>
					<p className="text-lg leading-relaxed mt-6">
						Con <strong>Applaby</strong>, tu negocio ya puede empezar a
						aprovechar estas tendencias de forma sencilla y sin grandes
						inversiones.
					</p>
				</section>

				<section className="bg-gray-50 rounded-2xl p-8 shadow-sm">
					<h2 className="text-2xl font-bold mb-6">En resumen</h2>
					<ul className="space-y-3 text-lg text-gray-700">
						<li>✔ Tener tu propia app te da control total sobre tu marca.</li>
						<li>✔ Ganas independencia de plataformas con comisiones.</li>
						<li>
							✔ Mejoras la experiencia del cliente con reservas y pedidos
							fáciles.
						</li>
						<li>
							✔ Fomentas la fidelización con cupones, notificaciones y programas
							de puntos.
						</li>
					</ul>
					<p className="mt-6 text-lg text-gray-700">
						Digitalizar tu negocio no es el futuro, es el presente. Con{" "}
						<strong>Applaby</strong> puedes empezar hoy mismo.
					</p>
				</section>

				<div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
					<h3 className="text-xl font-semibold mb-3 text-gray-900">
						¿Listo para dar el paso?
					</h3>
					<p className="text-gray-600 mb-5">
						Solicita una demo gratuita y descubre cómo tu negocio puede crecer
						gracias a la digitalización.
					</p>
					<a
						href="https://applaby.com/app-demo/"
						target="_blank"
						rel="noreferrer"
						className="inline-block bg-[#43bd98] text-white px-6 py-3 rounded-lg font-medium shadow hover:bg-[#37a885] transition"
					>
						Solicitar demo gratis
					</a>
				</div>
			</main>

			<BlogFooter />
		</div>
	);
}
